import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, BookText, Calculator, Check, Download, FileSpreadsheet, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../lib/api'
import { downloadFile } from '../lib/download'
import { formatCents } from '../lib/money'

interface Model111Result {
  year: number
  quarter: 1 | 2 | 3 | 4
  perceptorCount: number
  baseCents: number
  withheldCents: number
  totalToPayCents: number
  effectiveRatePct: number
  missingNifCount: number
  items: Array<{
    expenseId: string
    expenseNumber: string
    date: string
    supplierName: string
    supplierNif: string | null
    baseCents: number
    withheldCents: number
    ratePct: number
  }>
}

interface Model190Result {
  year: number
  perceptorCount: number
  totalBaseCents: number
  totalWithheldCents: number
  missingNifCount: number
  quarterlyTotals: [number, number, number, number]
  perceptors: Array<{
    nif: string | null
    name: string
    clave: string
    baseCents: number
    withheldCents: number
    quarters: [number, number, number, number]
  }>
}

interface Model303Result {
  year: number
  quarter: 1 | 2 | 3 | 4
  outputVatCents: number
  outputBaseCents: number
  inputVatCents: number
  inputBaseCents: number
  resultCents: number
}

interface Model347Party {
  nif: string
  name: string
  totalCents: number
  byQuarter: [number, number, number, number]
}

interface Model347Result {
  year: number
  threshold: number
  customers: Model347Party[]
  suppliers: Model347Party[]
}

const CURRENT_YEAR = new Date().getFullYear()

export default function AccountingAEAT() {
  const { t } = useTranslation('accounting')

  const QUARTERS: Array<{ value: 1 | 2 | 3 | 4; label: string }> = [
    { value: 1, label: t('quarters.t1') },
    { value: 2, label: t('quarters.t2') },
    { value: 3, label: t('quarters.t3') },
    { value: 4, label: t('quarters.t4') },
  ]

  const [year, setYear] = useState<number>(CURRENT_YEAR)
  const [quarter, setQuarter] = useState<1 | 2 | 3 | 4>(
    (Math.floor(new Date().getMonth() / 3) + 1) as 1 | 2 | 3 | 4
  )

  const [m303, setM303] = useState<Model303Result | null>(null)
  const [m347, setM347] = useState<Model347Result | null>(null)
  const [loading303, setLoading303] = useState(false)
  const [loading347, setLoading347] = useState(false)
  // F2 — retenciones de IRPF.
  const [m111, setM111] = useState<Model111Result | null>(null)
  const [m190, setM190] = useState<Model190Result | null>(null)
  const [error, setError] = useState<string | null>(null)

  const yearOptions = useMemo(() => {
    const arr: number[] = []
    for (let y = CURRENT_YEAR + 1; y >= CURRENT_YEAR - 6; y--) arr.push(y)
    return arr
  }, [])

  const load303 = useCallback(async () => {
    setLoading303(true)
    setError(null)
    try {
      const data = await api.get<Model303Result>(
        `/accounting/aeat/303?year=${year}&quarter=${quarter}`
      )
      setM303(data)
    } catch (err) {
      const text =
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : t('errors.calc303')
      setError(text)
      setM303(null)
    } finally {
      setLoading303(false)
    }
  }, [year, quarter, t])

  const load347 = useCallback(async () => {
    setLoading347(true)
    setError(null)
    try {
      const data = await api.get<Model347Result>(`/accounting/aeat/347?year=${year}`)
      setM347(data)
    } catch (err) {
      const text =
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : t('errors.calc347')
      setError(text)
      setM347(null)
    } finally {
      setLoading347(false)
    }
  }, [year, t])

  // F2 — 111 (trimestral) y 190 (anual). Fallan en silencio para no tapar el
  // resto de la página si algo va mal en un modelo concreto.
  const load111 = useCallback(async () => {
    try {
      setM111(await api.get<Model111Result>(`/accounting/aeat/111?year=${year}&quarter=${quarter}`))
    } catch {
      setM111(null)
    }
  }, [year, quarter])

  const load190 = useCallback(async () => {
    try {
      setM190(await api.get<Model190Result>(`/accounting/aeat/190?year=${year}`))
    } catch {
      setM190(null)
    }
  }, [year])

  useEffect(() => {
    void load111()
  }, [load111])
  useEffect(() => {
    void load190()
  }, [load190])

  useEffect(() => {
    void load303()
  }, [load303])
  useEffect(() => {
    void load347()
  }, [load347])

  const download303 = async () => {
    try {
      await downloadFile(
        `/accounting/aeat/303/csv?year=${year}&quarter=${quarter}`,
        `modelo-303-${year}-T${quarter}.csv`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.download'))
    }
  }

  const download347 = async () => {
    try {
      await downloadFile(
        `/accounting/aeat/347/csv?year=${year}`,
        `modelo-347-${year}.csv`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.download'))
    }
  }

  const download111 = async () => {
    try {
      await downloadFile(
        `/accounting/aeat/111/csv?year=${year}&quarter=${quarter}`,
        `modelo-111-${year}T${quarter}.csv`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.download'))
    }
  }

  const download190 = async () => {
    try {
      await downloadFile(`/accounting/aeat/190/csv?year=${year}`, `modelo-190-${year}.csv`)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.download'))
    }
  }

  const resultLabel = (cents: number): { text: string; tone: string } => {
    if (cents > 0) return { text: t('result.toPay'), tone: 'text-rose-700 dark:text-rose-300' }
    if (cents < 0) return { text: t('result.toOffset'), tone: 'text-emerald-700 dark:text-emerald-300' }
    return { text: t('result.zero'), tone: 't-muted' }
  }

  return (
    <div className="space-y-6 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Calculator size={20} className="text-toga-700 dark:text-toga-300" />
            {t('title')}
          </h1>
          <p className="text-xs t-muted">
            {t('subtitle')}
          </p>
        </div>

        <div className="flex items-end gap-2 flex-wrap">
          <label className="text-xs t-muted flex flex-col gap-1">
            {t('filters.year')}
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-toga-500/50"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs t-muted flex flex-col gap-1">
            {t('filters.quarter')}
            <select
              value={quarter}
              onChange={(e) => setQuarter(Number(e.target.value) as 1 | 2 | 3 | 4)}
              className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-toga-500/50"
            >
              {QUARTERS.map((q) => (
                <option key={q.value} value={q.value}>
                  {q.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {error && (
        <div className="surface p-3 flex items-start gap-2 border-rose-500/40">
          <AlertCircle size={16} className="text-rose-400 mt-0.5 shrink-0" />
          <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>
        </div>
      )}

      {/* ===================== Modelo 303 ===================== */}
      <section className="surface p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold t-primary flex items-center gap-2">
              <FileSpreadsheet size={18} className="text-toga-700 dark:text-toga-300" />
              {t('m303.title', { year, quarter })}
            </h2>
            <p className="text-xs t-muted">
              {t('m303.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={download303}
            disabled={loading303}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5 disabled:opacity-50"
          >
            <Download size={14} />
            {t('m303.downloadCsv')}
          </button>
        </div>

        {loading303 ? (
          <div className="t-muted flex items-center gap-2 text-sm">
            <Loader2 size={16} className="animate-spin" /> {t('calculating')}
          </div>
        ) : m303 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Card
              label={t('m303.outputBase')}
              value={formatCents(m303.outputBaseCents)}
              hint={t('m303.outputBaseHint')}
            />
            <Card
              label={t('m303.outputVat')}
              value={formatCents(m303.outputVatCents)}
              hint={t('m303.outputVatHint')}
            />
            <div />
            <Card
              label={t('m303.inputBase')}
              value={formatCents(m303.inputBaseCents)}
              hint={t('m303.inputBaseHint')}
            />
            <Card
              label={t('m303.inputVat')}
              value={formatCents(m303.inputVatCents)}
              hint={t('m303.inputVatHint')}
            />
            <Card
              label={t('m303.result')}
              value={formatCents(m303.resultCents)}
              hint={resultLabel(m303.resultCents).text}
              hintClass={resultLabel(m303.resultCents).tone}
              accent
            />
          </div>
        ) : (
          <p className="text-sm t-muted">{t('noData')}</p>
        )}
      </section>

      {/* ============= F2-B: Libros registro de IVA ============= */}
      <VatBooksSection year={year} quarter={quarter} />

      {/* ===================== Modelo 347 ===================== */}
      <section className="surface p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold t-primary flex items-center gap-2">
              <FileSpreadsheet size={18} className="text-toga-700 dark:text-toga-300" />
              {t('m347.title', { year })}
            </h2>
            <p className="text-xs t-muted">
              {t('m347.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={download347}
            disabled={loading347}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5 disabled:opacity-50"
          >
            <Download size={14} />
            {t('m347.downloadCsv')}
          </button>
        </div>

        {loading347 ? (
          <div className="t-muted flex items-center gap-2 text-sm">
            <Loader2 size={16} className="animate-spin" /> {t('calculating')}
          </div>
        ) : m347 ? (
          <div className="space-y-5">
            <PartyTable title={t('m347.customers')} parties={m347.customers} />
            <PartyTable title={t('m347.suppliers')} parties={m347.suppliers} />
          </div>
        ) : (
          <p className="text-sm t-muted">{t('noData')}</p>
        )}
      </section>
      {/* ---- F2: Modelo 111 (retenciones IRPF del trimestre) ---- */}
      <section
        className="rounded-xl border p-4 space-y-3"
        style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
      >
        <header className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold t-primary">{t('m111.title', { year, quarter })}</h2>
            <p className="text-xs t-muted">{t('m111.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => void download111()}
            className="px-2.5 py-1.5 rounded-md border border-base hover:bg-hover text-xs t-secondary"
          >
            {t('m111.download')}
          </button>
        </header>

        {m111 === null ? (
          <p className="text-xs t-muted">{t('m111.empty')}</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider t-faint">{t('m111.perceptors')}</div>
                <div className="font-mono t-primary">{m111.perceptorCount}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider t-faint">{t('m111.base')}</div>
                <div className="font-mono t-primary">{formatCents(m111.baseCents)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider t-faint">{t('m111.rate')}</div>
                <div className="font-mono t-primary">{m111.effectiveRatePct.toFixed(1)}%</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-rose-700 dark:text-rose-300">
                  {t('m111.toPay')}
                </div>
                <div className="font-mono font-semibold t-primary">{formatCents(m111.totalToPayCents)}</div>
              </div>
            </div>

            {m111.missingNifCount > 0 && (
              <div className="text-xs rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-700 dark:text-amber-300">
                {t('m111.missingNif', { count: m111.missingNifCount })}
              </div>
            )}

            {m111.items.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider t-faint">
                      <th className="py-1">{t('m111.col.date')}</th>
                      <th className="py-1">{t('m111.col.perceptor')}</th>
                      <th className="py-1">{t('m111.col.nif')}</th>
                      <th className="py-1 text-right">{t('m111.col.base')}</th>
                      <th className="py-1 text-right">{t('m111.col.rate')}</th>
                      <th className="py-1 text-right">{t('m111.col.withheld')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m111.items.map((i) => (
                      <tr key={i.expenseId} className="border-t border-base/40">
                        <td className="py-1 t-secondary text-xs">{i.date.slice(0, 10)}</td>
                        <td className="py-1 t-primary">{i.supplierName}</td>
                        <td className="py-1 font-mono text-xs t-muted">
                          {i.supplierNif ?? <span className="text-amber-700 dark:text-amber-300">{t('m111.noNif')}</span>}
                        </td>
                        <td className="py-1 text-right font-mono tabular-nums t-secondary">{formatCents(i.baseCents)}</td>
                        <td className="py-1 text-right font-mono tabular-nums t-muted">{i.ratePct.toFixed(1)}%</td>
                        <td className="py-1 text-right font-mono tabular-nums t-primary">{formatCents(i.withheldCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      {/* ---- F2: Modelo 190 (resumen anual por perceptor) ---- */}
      <section
        className="rounded-xl border p-4 space-y-3"
        style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
      >
        <header className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold t-primary">{t('m190.title', { year })}</h2>
            <p className="text-xs t-muted">{t('m190.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => void download190()}
            className="px-2.5 py-1.5 rounded-md border border-base hover:bg-hover text-xs t-secondary"
          >
            {t('m190.download')}
          </button>
        </header>

        {m190 === null || m190.perceptors.length === 0 ? (
          <p className="text-xs t-muted">{t('m190.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider t-faint">
                  <th className="py-1">{t('m190.col.nif')}</th>
                  <th className="py-1">{t('m190.col.perceptor')}</th>
                  <th className="py-1 text-center">{t('m190.col.clave')}</th>
                  <th className="py-1 text-right">{t('m190.col.base')}</th>
                  <th className="py-1 text-right">T1</th>
                  <th className="py-1 text-right">T2</th>
                  <th className="py-1 text-right">T3</th>
                  <th className="py-1 text-right">T4</th>
                  <th className="py-1 text-right">{t('m190.col.withheld')}</th>
                </tr>
              </thead>
              <tbody>
                {m190.perceptors.map((p) => (
                  <tr key={`${p.nif ?? p.name}`} className="border-t border-base/40">
                    <td className="py-1 font-mono text-xs t-muted">
                      {p.nif ?? <span className="text-amber-700 dark:text-amber-300">{t('m111.noNif')}</span>}
                    </td>
                    <td className="py-1 t-primary">{p.name}</td>
                    <td className="py-1 text-center font-mono text-xs t-muted">{p.clave}</td>
                    <td className="py-1 text-right font-mono tabular-nums t-secondary">{formatCents(p.baseCents)}</td>
                    {p.quarters.map((q, i) => (
                      <td key={i} className="py-1 text-right font-mono tabular-nums t-muted">
                        {q ? formatCents(q) : ''}
                      </td>
                    ))}
                    <td className="py-1 text-right font-mono tabular-nums font-semibold t-primary">
                      {formatCents(p.withheldCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-base font-semibold">
                  <td className="pt-2" colSpan={3}>{t('m190.total')}</td>
                  <td className="pt-2 text-right font-mono">{formatCents(m190.totalBaseCents)}</td>
                  {m190.quarterlyTotals.map((q, i) => (
                    <td key={i} className="pt-2 text-right font-mono">{q ? formatCents(q) : ''}</td>
                  ))}
                  <td className="pt-2 text-right font-mono">{formatCents(m190.totalWithheldCents)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

// ----------------- Componentes auxiliares -----------------

interface CardProps {
  label: string
  value: string
  hint?: string
  hintClass?: string
  accent?: boolean
}

function Card({ label, value, hint, hintClass, accent }: CardProps) {
  return (
    <div
      className={[
        'rounded-lg border border-base p-3 flex flex-col gap-1',
        accent ? 'bg-toga-500/10' : 'bg-transparent',
      ].join(' ')}
    >
      <span className="text-[11px] uppercase tracking-wider t-muted">{label}</span>
      <span className="text-lg font-semibold t-primary tabular-nums">{value}</span>
      {hint && <span className={`text-[11px] ${hintClass ?? 't-faint'}`}>{hint}</span>}
    </div>
  )
}

// ============================================================================
// F2-B — Libros registro de IVA (expedidas y recibidas).
//
// Lo que aporta sobre el 303 no es el total, es la fila: el 303 da cuatro
// cifras y el libro da las facturas que las sostienen. Y como el 303 se calcula
// del DIARIO y el libro de las FACTURAS, contrastarlos detecta lo que ningún
// informe aislado ve — una factura sin contabilizar, o un asiento manual con
// IVA que nadie registró como factura.
// ============================================================================

interface VatRateBreakdown {
  ratePct: number
  baseCents: number
  vatCents: number
}

interface VatBookRow {
  id: string
  issueDate: string
  recordDate?: string
  number: string
  partyName: string
  partyNif: string | null
  breakdown: VatRateBreakdown[]
  baseCents: number
  vatCents: number
  irpfCents: number
  totalCents: number
  kind: string
  deductibleVatCents?: number
}

interface VatBook {
  rows: VatBookRow[]
  totals: {
    baseCents: number
    vatCents: number
    irpfCents: number
    totalCents: number
    byRate: VatRateBreakdown[]
  }
}

interface VatBooksResult {
  year: number
  quarter: 1 | 2 | 3 | 4 | null
  issued: VatBook
  received: VatBook
  reconciliation: {
    bookOutputVatCents: number
    model303OutputVatCents: number
    outputDiffCents: number
    bookInputVatCents: number
    model303InputVatCents: number
    inputDiffCents: number
    matches: boolean
  }
}

function VatBooksSection({ year, quarter }: { year: number; quarter: 1 | 2 | 3 | 4 }) {
  const { t } = useTranslation('accounting')
  // El libro se pide por trimestre para cuadrarlo con el 303, y del año entero
  // para el cierre. Ambos casos son de uso real, así que se elige aquí.
  const [scope, setScope] = useState<'quarter' | 'year'>('quarter')
  const [data, setData] = useState<VatBooksResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const params = scope === 'quarter' ? `year=${year}&quarter=${quarter}` : `year=${year}`

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await api.get<VatBooksResult>(`/accounting/vat-books?${params}`))
    } catch (err) {
      const text =
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : t('vatBooks.errorLoad')
      setError(text)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [params, t])

  useEffect(() => {
    void load()
  }, [load])

  const download = async (book: 'issued' | 'received') => {
    const period = scope === 'quarter' ? `${year}T${quarter}` : String(year)
    const name = book === 'received' ? 'recibidas' : 'expedidas'
    try {
      await downloadFile(
        `/accounting/vat-books/csv?${params}&book=${book}`,
        `libro-iva-${name}-${period}.csv`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.download'))
    }
  }

  const rec = data?.reconciliation

  return (
    <section className="surface p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold t-primary flex items-center gap-2">
            <BookText size={18} className="text-toga-700 dark:text-toga-300" />
            {t('vatBooks.title')}
          </h2>
          <p className="text-xs t-muted">{t('vatBooks.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value as 'quarter' | 'year')}
            className="bg-transparent border border-base rounded px-2 py-1.5 text-sm t-secondary focus:outline-none focus:border-toga-500/50"
          >
            <option value="quarter">{t('vatBooks.scopeQuarter', { quarter })}</option>
            <option value="year">{t('vatBooks.scopeYear', { year })}</option>
          </select>
          <button
            type="button"
            onClick={() => void download('issued')}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 flex items-center gap-1.5 disabled:opacity-50"
          >
            <Download size={14} /> {t('vatBooks.downloadIssued')}
          </button>
          <button
            type="button"
            onClick={() => void download('received')}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 flex items-center gap-1.5 disabled:opacity-50"
          >
            <Download size={14} /> {t('vatBooks.downloadReceived')}
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-rose-700 dark:text-rose-300 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {loading ? (
        <div className="t-muted flex items-center gap-2 text-sm">
          <Loader2 size={16} className="animate-spin" /> {t('calculating')}
        </div>
      ) : data && rec ? (
        <div className="space-y-5">
          {/* Contraste con el 303 — lo primero que hay que mirar. */}
          <div
            className={[
              'rounded-lg border p-3 text-sm flex items-start gap-2',
              rec.matches
                ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-700 dark:text-emerald-300'
                : 'bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-300',
            ].join(' ')}
          >
            {rec.matches ? <Check size={15} className="mt-0.5 shrink-0" /> : <AlertCircle size={15} className="mt-0.5 shrink-0" />}
            <div>
              <p className="font-medium">
                {rec.matches ? t('vatBooks.reconOk') : t('vatBooks.reconMismatch')}
              </p>
              {!rec.matches && (
                <ul className="text-xs mt-1 space-y-0.5">
                  {rec.outputDiffCents !== 0 && (
                    <li>
                      {t('vatBooks.reconOutput', {
                        book: formatCents(rec.bookOutputVatCents),
                        model: formatCents(rec.model303OutputVatCents),
                        diff: formatCents(rec.outputDiffCents),
                      })}
                    </li>
                  )}
                  {rec.inputDiffCents !== 0 && (
                    <li>
                      {t('vatBooks.reconInput', {
                        book: formatCents(rec.bookInputVatCents),
                        model: formatCents(rec.model303InputVatCents),
                        diff: formatCents(rec.inputDiffCents),
                      })}
                    </li>
                  )}
                </ul>
              )}
            </div>
          </div>

          <VatBookTable title={t('vatBooks.issued')} book={data.issued} kind="issued" />
          <VatBookTable title={t('vatBooks.received')} book={data.received} kind="received" />
        </div>
      ) : (
        <p className="text-sm t-muted">{t('noData')}</p>
      )}
    </section>
  )
}

function VatBookTable({
  title,
  book,
  kind,
}: {
  title: string
  book: VatBook
  kind: 'issued' | 'received'
}) {
  const { t } = useTranslation('accounting')
  const isReceived = kind === 'received'

  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
        <h3 className="text-sm font-semibold t-secondary">
          {title} <span className="t-faint text-xs">({book.rows.length})</span>
        </h3>
        {book.totals.byRate.length > 0 && (
          <span className="text-[11px] t-faint">
            {book.totals.byRate
              .map((r) => `${r.ratePct}% · ${formatCents(r.baseCents)}`)
              .join('   ·   ')}
          </span>
        )}
      </div>

      {book.rows.length === 0 ? (
        <p className="text-xs t-muted">{t('vatBooks.noRows')}</p>
      ) : (
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0" style={{ background: 'var(--bg-card)' }}>
              <tr className="text-[11px] uppercase tracking-wider t-muted border-b border-base">
                <th className="text-left py-2 pr-3 font-medium">{t('vatBooks.cols.date')}</th>
                {isReceived && (
                  <th className="text-left py-2 pr-3 font-medium">{t('vatBooks.cols.recordDate')}</th>
                )}
                <th className="text-left py-2 pr-3 font-medium">{t('vatBooks.cols.number')}</th>
                <th className="text-left py-2 pr-3 font-medium">{t('vatBooks.cols.nif')}</th>
                <th className="text-left py-2 pr-3 font-medium">
                  {isReceived ? t('vatBooks.cols.supplier') : t('vatBooks.cols.customer')}
                </th>
                <th className="text-left py-2 pr-3 font-medium">{t('vatBooks.cols.rates')}</th>
                <th className="text-right py-2 px-2 font-medium">{t('vatBooks.cols.base')}</th>
                <th className="text-right py-2 px-2 font-medium">{t('vatBooks.cols.vat')}</th>
                <th className="text-right py-2 px-2 font-medium">{t('vatBooks.cols.irpf')}</th>
                <th className="text-right py-2 pl-2 font-medium">{t('vatBooks.cols.total')}</th>
              </tr>
            </thead>
            <tbody>
              {book.rows.map((r) => (
                <tr key={r.id} className="border-b border-base/50 last:border-0">
                  <td className="py-1.5 pr-3 t-secondary text-xs tabular-nums whitespace-nowrap">
                    {r.issueDate.slice(0, 10)}
                  </td>
                  {isReceived && (
                    <td className="py-1.5 pr-3 t-faint text-xs tabular-nums whitespace-nowrap">
                      {(r.recordDate ?? '').slice(0, 10)}
                    </td>
                  )}
                  <td className="py-1.5 pr-3 t-primary text-xs font-mono whitespace-nowrap">
                    {r.number || '—'}
                    {r.kind === 'RECTIFICATIVA' && (
                      <span className="ml-1 text-[10px] text-amber-700 dark:text-amber-300">R</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 t-secondary text-xs tabular-nums">
                    {r.partyNif ?? (
                      <span className="text-amber-700 dark:text-amber-300">
                        {t('vatBooks.noNif')}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 t-primary text-xs max-w-[16rem] truncate" title={r.partyName}>
                    {r.partyName}
                  </td>
                  <td className="py-1.5 pr-3 t-faint text-[11px] whitespace-nowrap">
                    {r.breakdown.map((b) => `${b.ratePct}%`).join(' + ') || '—'}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums t-secondary text-xs">
                    {formatCents(r.baseCents)}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums t-secondary text-xs">
                    {formatCents(r.vatCents)}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums t-faint text-xs">
                    {r.irpfCents ? formatCents(r.irpfCents) : ''}
                  </td>
                  <td className="py-1.5 pl-2 text-right tabular-nums font-semibold t-primary text-xs">
                    {formatCents(r.totalCents)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold t-primary border-t border-base">
                <td className="py-2 pr-3 text-xs" colSpan={isReceived ? 6 : 5}>
                  {t('vatBooks.cols.totals')}
                </td>
                <td className="py-2 px-2 text-right tabular-nums text-xs">
                  {formatCents(book.totals.baseCents)}
                </td>
                <td className="py-2 px-2 text-right tabular-nums text-xs">
                  {formatCents(book.totals.vatCents)}
                </td>
                <td className="py-2 px-2 text-right tabular-nums text-xs">
                  {formatCents(book.totals.irpfCents)}
                </td>
                <td className="py-2 pl-2 text-right tabular-nums text-xs">
                  {formatCents(book.totals.totalCents)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}

function PartyTable({ title, parties }: { title: string; parties: Model347Party[] }) {
  const { t } = useTranslation('accounting')
  return (
    <div>
      <h3 className="text-sm font-semibold t-secondary mb-2">
        {title} <span className="t-faint text-xs">({parties.length})</span>
      </h3>
      {parties.length === 0 ? (
        <p className="text-xs t-muted">{t('m347.noOps')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider t-muted border-b border-base">
                <th className="text-left py-2 pr-3 font-medium">{t('m347.cols.nif')}</th>
                <th className="text-left py-2 pr-3 font-medium">{t('m347.cols.name')}</th>
                <th className="text-right py-2 px-2 font-medium">{t('m347.cols.t1')}</th>
                <th className="text-right py-2 px-2 font-medium">{t('m347.cols.t2')}</th>
                <th className="text-right py-2 px-2 font-medium">{t('m347.cols.t3')}</th>
                <th className="text-right py-2 px-2 font-medium">{t('m347.cols.t4')}</th>
                <th className="text-right py-2 pl-2 font-medium">{t('m347.cols.total')}</th>
              </tr>
            </thead>
            <tbody>
              {parties.map((p) => (
                <tr key={p.nif + p.name} className="border-b border-base/50 last:border-0">
                  <td className="py-2 pr-3 t-secondary tabular-nums">{p.nif}</td>
                  <td className="py-2 pr-3 t-primary">{p.name}</td>
                  <td className="py-2 px-2 text-right tabular-nums t-secondary">
                    {formatCents(p.byQuarter[0])}
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums t-secondary">
                    {formatCents(p.byQuarter[1])}
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums t-secondary">
                    {formatCents(p.byQuarter[2])}
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums t-secondary">
                    {formatCents(p.byQuarter[3])}
                  </td>
                  <td className="py-2 pl-2 text-right tabular-nums font-semibold t-primary">
                    {formatCents(p.totalCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  )
}
